import { io, Socket } from 'socket.io-client';
import { regionStore } from '../../store/jotai/store';
import {
  messagesAtom,
  conversationsAtom,
  totalUnreadAtom
} from '../../store/jotai/atoms/conversations.atom';
import { StrapiChatMessage } from '../models/strapi-chat-message.model';
import { StrapiConversation } from '../models/strapi-conversation.model';
import { chatService } from '../../services/chat-service';
import { Logger } from '../logger/Logger';

const lastMessageFields = (m: StrapiChatMessage) => ({
  lastMessageContent: m.content,
  lastMessageSenderType: m.senderType,
  lastMessageAt: m.createdAt
});

let socket: Socket | null = null;

// Tracks which conversation the user is actively viewing (set by ChatWindow).
// Using a module variable rather than the Jotai atom avoids stale reads after
// the user navigates away (the atom is not cleared on unmount).
let currentOpenConversationId: number | null = null;

export const setCurrentConversation = (id: number | null): void => {
  currentOpenConversationId = id;
};

export const initSocket = (): Socket => {
  if (socket) {
    socket.disconnect();
  }

  // Auth travels via the httpOnly session cookie on the WS upgrade
  // (withCredentials); the backend reads it from the handshake headers.
  socket = io(process.env.REACT_APP_STRAPI_ENDPOINT as string, {
    withCredentials: true,
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000
  });

  // A new conversation was started by a user contacting this user's vendor —
  // prepend it to asVendor so the ConversationList shows it immediately.
  socket.on('conversation:new', ({ conversation }: { conversation: StrapiConversation }) => {
    const conversations = regionStore.get(conversationsAtom);
    const alreadyExists = conversations.asVendor.some((c) => c.id === conversation.id);
    if (!alreadyExists) {
      regionStore.set(conversationsAtom, {
        ...conversations,
        asVendor: [conversation, ...conversations.asVendor]
      });
    }
  });

  // Self-heal on every successful (re)connection. `connect` fires on the initial
  // connect AND after each automatic reconnect, so it covers a delayed/retried
  // first attempt too — the user no longer has to open Messages to "kick" the
  // socket. (In socket.io v4 the `reconnect` event fires on the Manager, not the
  // socket, so `socket.on('reconnect')` never fired — `connect` is the reliable
  // unified hook.) The backend auto-joins user:{id} on every 'connection', so
  // here we only rejoin the actively-open conversation room and re-sync the
  // conversation list (also populating it after login, where it isn't fetched).
  socket.on('connect', () => {
    if (currentOpenConversationId) {
      socket?.emit('join:conversation', currentOpenConversationId);
      chatService.getMessages(currentOpenConversationId);
    }
    chatService.getConversations().catch(() => {});
  });

  socket.on('connect_error', (error) => {
    Logger.error('Socket connect_error:', error?.message ?? error);
  });

  socket.on(
    'message:new',
    ({ message, conversationId }: { message: StrapiChatMessage; conversationId: number }) => {
      // Always update the last-message preview in ConversationList
      const conversations = regionStore.get(conversationsAtom);
      regionStore.set(conversationsAtom, {
        asUser: conversations.asUser.map((c) =>
          c.id === conversationId ? { ...c, ...lastMessageFields(message) } : c
        ),
        asVendor: conversations.asVendor.map((c) =>
          c.id === conversationId ? { ...c, ...lastMessageFields(message) } : c
        )
      });

      if (currentOpenConversationId === conversationId) {
        // User is viewing this conversation — append to thread and mark as read
        const current = regionStore.get(messagesAtom);
        if (!current.find((m) => m.id === message.id)) {
          regionStore.set(messagesAtom, [...current, message]);
        }
        chatService.markAsRead(conversationId);
      }
    }
  );

  // Backend pushes the authoritative unread total whenever a new message arrives.
  // If the update is for the currently active conversation, skip it — markAsRead
  // already keeps that conversation's count at 0.
  socket.on(
    'unread:update',
    ({
      total,
      conversationId,
      conversationUnread,
      senderType,
      message
    }: {
      total: number;
      conversationId?: number;
      conversationUnread?: number;
      senderType?: 'user' | 'vendor';
      message?: StrapiChatMessage;
    }) => {
      if (conversationId && currentOpenConversationId === conversationId) {
        return;
      }

      regionStore.set(totalUnreadAtom, total);

      // Update the per-conversation unread badge and message preview in ConversationList
      if (conversationId !== undefined && conversationUnread !== undefined && senderType) {
        const conversations = regionStore.get(conversationsAtom);
        const patch = (c: StrapiConversation) => ({
          ...c,
          ...(message ? lastMessageFields(message) : {})
        });
        regionStore.set(conversationsAtom, {
          asUser: conversations.asUser.map((c) =>
            c.id === conversationId && senderType === 'vendor'
              ? { ...patch(c), userUnreadCount: conversationUnread }
              : c
          ),
          asVendor: conversations.asVendor.map((c) =>
            c.id === conversationId && senderType === 'user'
              ? { ...patch(c), vendorUnreadCount: conversationUnread }
              : c
          )
        });
      }
    }
  );

  return socket;
};

export const getSocket = (): Socket | null => socket;

export const disconnectSocket = (): void => {
  socket?.disconnect();
  socket = null;
};

export const joinConversation = (id: number): void => {
  socket?.emit('join:conversation', id);
};

export const leaveConversation = (id: number): void => {
  socket?.emit('leave:conversation', id);
};
